import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import {
  createApplication,
  listCandidates,
  listJobPostings,
} from '@/features/recruitment/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const schema = z.object({
  candidate_id: z.string().trim().min(1, 'Choose a candidate'),
  job_posting_id: z.string().trim().min(1, 'Choose a job posting'),
  notes: z.string().trim().max(4000),
})

type FormValues = z.infer<typeof schema>

/** Opens an application -- always at `applied`, the pipeline's one entry point. There is
 *  no edit mode: once open, a stage only moves through `ApplicationDetail`'s stage-advance
 *  action, never through this form -- see `recruitment_service.transition_stage`. */
export function ApplicationFormDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [formError, setFormError] = useState<string | null>(null)

  const candidates = useQuery({
    queryKey: ['candidates', 'picker'],
    queryFn: () => listCandidates({ page: 1 }),
    retry: false,
    enabled: open,
  })
  const postings = useQuery({
    queryKey: ['job-postings', 'picker', 'open'],
    queryFn: () => listJobPostings({ status: 'open' }),
    retry: false,
    enabled: open,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { candidate_id: '', job_posting_id: '', notes: '' },
  })

  const guard = useDirtyGuard(open && isDirty)

  const close = () => {
    guard.attempt(() => {
      setFormError(null)
      reset()
      onClose()
    })
  }

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      createApplication({
        candidate_id: values.candidate_id,
        job_posting_id: values.job_posting_id,
        notes: values.notes || null,
      }),
    onSuccess: (application) => {
      void queryClient.invalidateQueries({ queryKey: ['applications'] })
      toast.success('Application opened.')
      reset()
      onClose()
      void navigate(`/recruitment/applications/${application.id}`)
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.status === 409
            ? 'This candidate already has an open application to that job posting.'
            : error.message
          : 'Could not reach the server. Try again.',
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title="Open an application"
        description="Links a candidate to a job posting, starting at Applied."
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form="application-form" loading={isSubmitting}>
              Open application
            </Button>
          </>
        }
      >
        <form id="application-form" onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          <Field label="Candidate" error={errors.candidate_id?.message}>
            {(props) => (
              <select
                {...props}
                {...register('candidate_id')}
                className={SELECT_CLASS}
                disabled={candidates.isLoading}
              >
                <option value="">Choose a candidate…</option>
                {(candidates.data?.data ?? []).map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.full_name} ({candidate.email})
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field
            label="Job posting"
            error={errors.job_posting_id?.message}
            hint="Only open postings are listed."
          >
            {(props) => (
              <select
                {...props}
                {...register('job_posting_id')}
                className={SELECT_CLASS}
                disabled={postings.isLoading}
              >
                <option value="">Choose a job posting…</option>
                {(postings.data?.data ?? []).map((posting) => (
                  <option key={posting.id} value={posting.id}>
                    {posting.title}
                    {posting.department_name ? ` · ${posting.department_name}` : ''}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Notes" error={errors.notes?.message} hint="Optional.">
            {(props) => <Input {...props} {...register('notes')} autoComplete="off" />}
          </Field>
        </form>
      </Modal>

      <UnsavedChangesDialog guard={guard} />
    </>
  )
}
